<template>
  <div class="platform-auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <div class="logo">
          <img src="@/assets/omind_logo.png" alt="OMIND" class="logo-img" />
        </div>
        <h1>Platform Admin</h1>
        <p class="subtitle">Multi-Tenant Administration Console</p>
      </div>

      <div class="auth-tabs">
        <button
          :class="['tab', { active: activeTab === 'login' }]"
          @click="activeTab = 'login'"
        >
          Login
        </button>
        <button
          :class="['tab', { active: activeTab === 'register' }]"
          @click="activeTab = 'register'"
        >
          Register
        </button>
      </div>

      <!-- Login Form -->
      <form v-if="activeTab === 'login'" @submit.prevent="handleLogin" class="auth-form">
        <div class="form-group">
          <label for="email">Email</label>
          <input
            type="email"
            id="email"
            v-model="loginForm.email"
            placeholder="admin@omind.com"
            required
            :disabled="loading"
          />
        </div>

        <div class="form-group">
          <label for="password">Password</label>
          <input
            type="password"
            id="password"
            v-model="loginForm.password"
            placeholder="••••••••"
            required
            :disabled="loading"
          />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <button type="submit" class="submit-btn" :disabled="loading">
          <span v-if="loading">Logging in...</span>
          <span v-else>Login</span>
        </button>
      </form>

      <!-- Register Form -->
      <form v-if="activeTab === 'register'" @submit.prevent="handleRegister" class="auth-form">
        <div class="form-group">
          <label for="reg-name">Name</label>
          <input
            type="text"
            id="reg-name"
            v-model="registerForm.name"
            placeholder="Your full name"
            required
            :disabled="loading"
          />
        </div>

        <div class="form-group">
          <label for="reg-email">Email</label>
          <input
            type="email"
            id="reg-email"
            v-model="registerForm.email"
            :placeholder="`you@${adminDomain}`"
            required
            :disabled="loading"
          />
          <small class="hint">Must use @{{ adminDomain }} domain</small>
        </div>

        <div class="form-group">
          <label for="reg-password">Password</label>
          <input
            type="password"
            id="reg-password"
            v-model="registerForm.password"
            placeholder="Minimum 8 characters"
            required
            minlength="8"
            :disabled="loading"
          />
        </div>

        <div class="form-group">
          <label for="reg-confirm">Confirm Password</label>
          <input
            type="password"
            id="reg-confirm"
            v-model="registerForm.confirmPassword"
            placeholder="Confirm password"
            required
            :disabled="loading"
          />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="success" class="success-message">
          {{ success }}
        </div>

        <button type="submit" class="submit-btn" :disabled="loading">
          <span v-if="loading">Registering...</span>
          <span v-else>Register</span>
        </button>
      </form>

      <div class="auth-footer">
        <p>Platform administration for multi-tenant environments</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

// Same-origin / reverse-proxy path as adminApi (no env base URL)
const platformAxios = axios.create({
  baseURL: '/content-management-api',
});

export default {
  name: 'PlatformLogin',
  data() {
    return {
      activeTab: 'login',
      loading: false,
      error: null,
      success: null,
      adminDomain: 'omind.com', // Will be fetched from config
      loginForm: {
        email: '',
        password: '',
      },
      registerForm: {
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
      },
    };
  },
  methods: {
    async handleLogin() {
      this.error = null;
      this.loading = true;

      try {
        const response = await platformAxios.post('/auth/platform/login', {
          email: this.loginForm.email,
          password: this.loginForm.password,
        });

        if (response.data.success) {
          // Store token
          localStorage.setItem('platform_token', response.data.token);
          localStorage.setItem('platform_refresh_token', response.data.refresh_token);
          localStorage.setItem('platform_user', JSON.stringify(response.data.user));

          // Redirect to platform dashboard
          this.$router.push('/platform');
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Login failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async handleRegister() {
      this.error = null;
      this.success = null;
      this.loading = true;

      // Validate passwords match
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.error = 'Passwords do not match';
        this.loading = false;
        return;
      }

      try {
        const response = await platformAxios.post('/auth/platform/register', {
          name: this.registerForm.name,
          email: this.registerForm.email,
          password: this.registerForm.password,
        });

        if (response.data.success) {
          this.success = 'Registration successful! You can now login.';
          this.activeTab = 'login';
          this.loginForm.email = this.registerForm.email;

          // Clear register form
          this.registerForm = {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
          };
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Registration failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.platform-auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f8f8 0%, #ffffff 50%, #f8f8f8 100%);
  padding: 20px;
  font-family: 'AsenPro', sans-serif;
}

.auth-card {
  background: #ffffff;
  border: 1px solid #e7e7e7;
  border-radius: 2rem;
  padding: 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.logo {
  margin-bottom: 16px;
}

.logo-img {
  width: 160px;
  height: auto;
}

.auth-header h1 {
  color: #1E2019;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.subtitle {
  color: #666666;
  font-size: 14px;
  margin: 8px 0 0;
}

.auth-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
}

.tab {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: #f4f4f4;
  color: #666666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 2rem;
  transition: all 0.2s;
}

.tab:hover {
  background: rgba(228, 130, 26, 0.1);
  color: #e4821a;
}

.tab.active {
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: #fff;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #1E2019;
  font-size: 13px;
  font-weight: 500;
}

.form-group input {
  padding: 14px 16px;
  border: 1px solid #e7e7e7;
  border-radius: 0.75rem;
  background: #f8f8f8;
  color: #1E2019;
  font-size: 15px;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #e4821a;
  background: #ffffff;
}

.form-group input::placeholder {
  color: #999999;
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hint {
  color: #999999;
  font-size: 12px;
}

.error-message {
  background: rgba(228, 75, 85, 0.1);
  border: 1px solid rgba(228, 75, 85, 0.3);
  color: #e44b55;
  padding: 12px 16px;
  border-radius: 0.75rem;
  font-size: 13px;
}

.success-message {
  background: rgba(174, 234, 183, 0.2);
  border: 1px solid rgba(174, 234, 183, 0.5);
  color: #2d8a3e;
  padding: 12px 16px;
  border-radius: 0.75rem;
  font-size: 13px;
}

.submit-btn {
  padding: 14px 24px;
  border: none;
  border-radius: 2rem;
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(228, 130, 26, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-footer {
  margin-top: 30px;
  text-align: center;
}

.auth-footer p {
  color: #999999;
  font-size: 12px;
  margin: 0;
}
</style>
